// Mercapoli - Admin Tipos de Comercio
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Store, Search, Loader2, Plus, Edit, Trash2,
  GripVertical, ToggleLeft, ToggleRight
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { admin as adminApi } from '../../lib/api';
import { toast } from 'sonner';

const AdminTiposComercio = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tipos, setTipos] = useState([]);
  const [filtro, setFiltro] = useState('');
  
  // Modal de crear/editar
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    icono: '🏪',
    color: '#00BFA6',
    orden: 0
  });
  const [saving, setSaving] = useState(false);
  
  // Dialog de eliminar
  const [deleteDialog, setDeleteDialog] = useState({ open: false, tipo: null });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const data = await adminApi.getTiposComercio();
      setTipos(data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar tipos de comercio');
    }
    setLoading(false);
  };

  const openCreateModal = () => {
    setEditando(null);
    setFormData({
      nombre: '',
      descripcion: '',
      icono: '🏪',
      color: '#00BFA6',
      orden: tipos.length + 1
    });
    setShowModal(true);
  };

  const openEditModal = (tipo) => {
    setEditando(tipo);
    setFormData({
      nombre: tipo.nombre,
      descripcion: tipo.descripcion || '',
      icono: tipo.icono || '🏪',
      color: tipo.color || '#00BFA6',
      orden: tipo.orden || 0
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.nombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    
    setSaving(true);
    try {
      if (editando) {
        await adminApi.updateTipoComercio(editando.tipo_id, formData);
        toast.success('Tipo de comercio actualizado');
      } else {
        await adminApi.createTipoComercio(formData);
        toast.success('Tipo de comercio creado');
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!deleteDialog.tipo) return;
    
    try {
      await adminApi.deleteTipoComercio(deleteDialog.tipo.tipo_id);
      toast.success('Tipo de comercio desactivado');
      setDeleteDialog({ open: false, tipo: null });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const toggleEstado = async (tipo) => {
    try {
      await adminApi.updateTipoComercio(tipo.tipo_id, { activo: !tipo.activo });
      toast.success(`Tipo ${!tipo.activo ? 'activado' : 'desactivado'}`);
      loadData();
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const filtered = tipos.filter(t =>
    !filtro || t.nombre?.toLowerCase().includes(filtro.toLowerCase())
  );

  // Colores predefinidos para selector
  const coloresPreset = [
    '#FF6B35', '#00BFA6', '#4A90D9', '#7CB342', 
    '#E91E63', '#8E24AA', '#FF8F00', '#5D4037'
  ];

  // Iconos sugeridos
  const iconosSugeridos = ['🍽️', '💊', '🏪', '🥬', '💐', '🍷', '🥐', '🐾', '📦', '🛒', '🎁', '🧴'];

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="container-app">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/admin')} className="p-2 -ml-2 rounded-lg hover:bg-muted">
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h1 className="font-heading text-lg font-bold">Tipos de Comercio</h1>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline">{tipos.length} tipos</Badge>
              <Button 
                onClick={openCreateModal}
                className="bg-brand-primary hover:bg-brand-secondary"
                data-testid="new-tipo-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Nuevo Tipo
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container-app py-6">
        {/* Búsqueda */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar tipo de comercio..."
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            className="pl-10"
          />
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <Store className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">No hay tipos de comercio</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={openCreateModal}
            >
              <Plus className="h-4 w-4 mr-2" />
              Crear primer tipo
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((tipo, index) => (
              <Card key={tipo.tipo_id} className={`hover:shadow-md transition-shadow ${!tipo.activo ? 'opacity-50' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="text-muted-foreground cursor-move">
                      <GripVertical className="h-5 w-5" />
                    </div>
                    
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
                      style={{ backgroundColor: `${tipo.color}20` }}
                    >
                      {tipo.icono || '🏪'}
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium">{tipo.nombre}</h3>
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: tipo.color }}
                        />
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {tipo.descripcion || 'Sin descripción'}
                      </p>
                    </div>
                    
                    <Badge variant="outline" className="ml-auto">
                      {tipo.total_comercios || 0} comercios
                    </Badge>
                    
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => openEditModal(tipo)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      
                      <button
                        onClick={() => toggleEstado(tipo)}
                        className={`p-2 rounded-lg transition-colors ${
                          tipo.activo 
                            ? 'text-brand-primary hover:bg-brand-primary/10' 
                            : 'text-muted-foreground hover:bg-muted'
                        }`}
                      >
                        {tipo.activo ? (
                          <ToggleRight className="h-5 w-5" />
                        ) : (
                          <ToggleLeft className="h-5 w-5" />
                        )}
                      </button>
                      
                      {tipo.total_comercios === 0 && (
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          onClick={() => setDeleteDialog({ open: true, tipo })}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Modal Crear/Editar */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editando ? 'Editar Tipo de Comercio' : 'Nuevo Tipo de Comercio'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nombre *</Label>
              <Input
                value={formData.nombre}
                onChange={(e) => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
                placeholder="Ej: Restaurantes, Farmacias..."
                data-testid="tipo-nombre"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Descripción</Label>
              <Input
                value={formData.descripcion}
                onChange={(e) => setFormData(prev => ({ ...prev, descripcion: e.target.value }))}
                placeholder="Breve descripción del tipo de comercio"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Icono</Label>
              <div className="flex items-center gap-2">
                <Input
                  value={formData.icono}
                  onChange={(e) => setFormData(prev => ({ ...prev, icono: e.target.value }))}
                  className="w-20 text-center text-2xl"
                />
                <div className="flex flex-wrap gap-1">
                  {iconosSugeridos.map(icono => (
                    <button
                      key={icono}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, icono }))}
                      className={`w-8 h-8 rounded hover:bg-muted flex items-center justify-center text-lg ${formData.icono === icono ? 'bg-brand-primary/20 ring-2 ring-brand-primary' : ''}`}
                    >
                      {icono}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Color</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                  className="w-12 h-10 p-1 cursor-pointer"
                />
                <div className="flex gap-1">
                  {coloresPreset.map(color => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, color }))}
                      className={`w-8 h-8 rounded-full border-2 ${formData.color === color ? 'border-foreground' : 'border-transparent'}`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Orden de aparición</Label>
              <Input
                type="number"
                min="0"
                value={formData.orden}
                onChange={(e) => setFormData(prev => ({ ...prev, orden: parseInt(e.target.value) || 0 }))}
                className="w-24"
              />
            </div>
            
            {/* Preview */}
            <div className="p-4 bg-muted rounded-lg">
              <p className="text-xs text-muted-foreground mb-2">Vista previa:</p>
              <div className="flex items-center gap-3">
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
                  style={{ backgroundColor: `${formData.color}20` }}
                >
                  {formData.icono}
                </div>
                <div>
                  <p className="font-medium">{formData.nombre || 'Nombre del tipo'}</p>
                  <p className="text-sm text-muted-foreground">{formData.descripcion || 'Descripción'}</p>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSave}
              disabled={saving}
              className="bg-brand-primary hover:bg-brand-secondary"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editando ? 'Guardar Cambios' : 'Crear Tipo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de eliminar */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, tipo: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar tipo de comercio?</AlertDialogTitle>
            <AlertDialogDescription>
              El tipo "{deleteDialog.tipo?.nombre}" será eliminado permanentemente.
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminTiposComercio;
